import { Form, IFilterReference, IParam, MxObject, SortSpecifies } from "./interfaces";
//#region ------------------------------------- GENERAL
async function mxAction(
    microflow: string, guids: string[], showProgress?: boolean, showErrorInPopup: boolean = false, progressMsg?: string
): Promise<any | undefined> {
    return new Promise(resolve => {
        let progressId: number | undefined;
        if (showProgress) {
            progressId = window.mx.ui.showProgress(progressMsg, true);
        }
        let params: any = {
            actionname: microflow,
            applyto: "selection",
            guids
        };
        mx.data.action({
            params,
            context: new mendix.lib.MxContext(),
            callback: (result: any) => {
                progressId && window.mx.ui.hideProgress(progressId);
                resolve(result);
            },
            error: (error: any) => {
                if (showErrorInPopup && error.message) {
                    mx.ui.error(error.message);
                }
                progressId && window.mx.ui.hideProgress(progressId);
                resolve(undefined);
            }
        });
    });
}
//#endregion
//#region ------------------------------------- CREATE
/** Function to create new Mendix object.
 * - Return the object if successful
 * - Return undefined if failed
 */
export function createNewObject(entity: string): Promise<MxObject | undefined> {
    return new Promise(resolve => {
        mx.data.create({
            entity,
            callback: (object: MxObject) => resolve(object),
            error: (error) => {
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}

/** Function to create then commit a Mendix object.
 * - Return the object if successful
 * - Return undefined if failed
 */
export async function createThenCommit(entity: string, paramList: IParam[]): Promise<MxObject | undefined> {
    const newEntity = await createNewObject(entity);
    if (newEntity) {
        const updatedEntity = await commitObject(newEntity, paramList);
        return updatedEntity;
    }
    return undefined;
}
//#endregion
//#region ------------------------------------- COMMIT
/** Function to commit a Mendix object.
 * - Return the object if successful
 * - Return undefined if failed
 * 
 * Note: commit function of mx.data doesn't return a result 
 */
export function commitObject(mxobj: MxObject, paramList: IParam[]): Promise<MxObject | undefined> {
    return new Promise(resolve => {
        for (const item of paramList) {
            if (mxobj.isObjectReferenceSet(item.paramName)) {
                if (Array.isArray(item.paramValue)) {
                    item.paramValue.forEach(value => {
                        if (value !== "") { mxobj.addReference(item.paramName, value); }
                    });
                } else {
                    mxobj.addReference(item.paramName, item.paramValue);
                }
            } else {
                mxobj.set(item.paramName, item.paramValue);
            }
        }
        mx.data.commit({
            mxobj,
            callback: () => resolve(mxobj),
            error: (error) => {
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}
/** Function to commit a Mendix object by Id
 * - Return the object if successful
 * - Return undefined if failed
 */
export async function commitObjectById(entityId: string, paramList: IParam[]): Promise<MxObject | undefined> {
    const entity = await retrieveObjectById(entityId, false);
    if (entity) {
        const updatedEntity = await commitObject(entity, paramList);
        return updatedEntity;
    }
    return undefined;
}
//#endregion
//#region ------------------------------------- DELETE
/** Function to delete a Mendix object by Id
 * - Return the id if successful
 * - Return undefined if failed
 */
export async function deleteObjectById(guid: string): Promise<string | undefined> {
    return new Promise(resolve => {
        mx.data.remove({
            guid,
            callback: () => resolve(guid),
            error: (error) => {
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}
export function deleteObjectWithConfirmation(
    content: string, guid: string, callback?: (id: string | undefined) => void
) {
    callConfirmation(content, async () => {
        const id = await deleteObjectById(guid);
        callback && callback(id);
    });
}
//#endregion
//#region ------------------------------------- RETRIEVE
/** Function to retrieve a Mendix object by Id
 * - Return the first object if successful
 * - Return undefined if failed
 */
export function retrieveObjectById(guid: string, noCache?: boolean): Promise<MxObject | undefined> {
    return new Promise(resolve => {
        mx.data.get({
            guids: [guid],
            noCache: noCache === undefined ? noCache : true,
            callback: (objects: MxObject[]) => resolve(objects[0]),
            error: (error) => {
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}
export async function retrieveObjectsByXpathAndId(
    entityName: string, guid: string, attributes: string[], references?: IFilterReference, sortSpec?: SortSpecifies, limit?: number
): Promise<MxObject | undefined> {
    const xPath = `//${entityName}[id=${guid}]`;
    const results = await retrieveObjectsByXpath(xPath, attributes, references, sortSpec, limit);
    return results ? results[0] : undefined;
}

/** Function to retrieve a Mendix object by Xpath
 * - Return the array of objects if successful
 * - Return undefined if failed
 */
export async function retrieveObjectsByXpath(
    xpathString: string, attributes: string[], references?: IFilterReference, sortSpec?: SortSpecifies, limit?: number
): Promise<MxObject[] | undefined> {
    let filter: any = { attributes };
    if (references && Object.keys(references).length > 0) {
        filter = { ...filter, references };
    }
    if (sortSpec) {
        filter = { ...filter, sort: sortSpec };
    }
    if (limit && limit > 0) {
        filter = { ...filter, amount: limit };
    }
    return new Promise(resolve => {
        mx.data.get({
            xpath: xpathString,
            noCache: true,
            filter,
            callback: (objects: MxObject[]) => resolve(objects),
            error: (error) => {
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}
//#endregion
//#region ------------------------------------- NANOFLOW
export async function callNanoflow(
    nanoflow: mx.Nanoflow, entityType: string, entity: MxObject, showProgress?: boolean
): Promise<any> {
    return new Promise(resolve => {
        const context = setupContext(entity, entityType);
        const pid = showProgress ? mx.ui.showProgress("", true) : undefined;
        mx.data.callNanoflow({
            nanoflow,
            // @ts-ignore
            origin: undefined,
            context,
            callback: (result: any) => {
                pid && mx.ui.hideProgress(pid);
                resolve(result);
            },
            error: (error) => {
                pid && mx.ui.hideProgress(pid);
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}
//#endregion
//#region ------------------------------------- MICROFLOW
export async function callMicroflow(microflow: string, guid: string, showProgress = false, showErrorInPopup = false, progressMsg?: string) {
    return mxAction(microflow, [guid], showProgress, showErrorInPopup, progressMsg);
}
export function callMicroflowWithConfirmation(
    content: string, mf: string, guid: string, showProgress = false, showErrorInPopup = false, callback?: (param: any) => any
) {
    callConfirmation(content, async () => {
        const result = await callMicroflow(mf, guid, showProgress, showErrorInPopup);
        callback && callback(result);
    });
}
export async function callMicroflows(microflows: string[], guid: string): Promise<any[]> {
    const promises = microflows.filter(microflow => microflow !== "")
        .map(microflow => callMicroflow(microflow, guid));
    const results = await Promise.all(promises);
    return results;
}
export async function callMicroflowsWithSpecificCallback(
    guid: string, microflows: Array<{ mf: string, callback?: (result: any) => void }>
): Promise<any[]> {
    const promises: Array<Promise<any>> = microflows.filter(microflow => microflow.mf !== "")
        .map(microflow => {
            const { mf, callback } = microflow;
            return callMicroflow(mf, guid).then(result => callback && callback(result));
        });
    const results = await Promise.all(promises);
    return results;
}
export async function callMicroflowWithHelper(
    microflowName: string, helperEntityName: string, paramList: IParam[], showProgress?: boolean, showErrorInPopup = false, progressMsg?: string
): Promise<any> {
    const helperEntity = await createThenCommit(helperEntityName, paramList);
    if (helperEntity) {
        return callMicroflow(microflowName, helperEntity.getGuid(), showProgress, showErrorInPopup, progressMsg);
    }
    return undefined;
}
//#endregion
//#region ------------------------------------- CONFIRMATION
export function callConfirmation(
    content: string, handler: () => any = () => { }, proceedTitle?: string, cancelTitle?: string
) {
    mx.ui.confirmation({
        content,
        proceed: proceedTitle ? proceedTitle : "Yes",
        cancel: cancelTitle ? cancelTitle : "No",
        handler
    });
}
//#endregion
//#region ------------------------------------- FORM
function setupContext(entity: MxObject, entityType?: string): mendix.lib.MxContext {
    const context = new mendix.lib.MxContext();
    entityType && context.setContext(entityType, entity.getGuid());
    context.setTrackObject(entity);
    return context;
}
export function openForm(formPath: string, entity: MxObject, formTitle?: string, callback?: (form: Form) => void) {
    mx.ui.openForm(formPath, {
        location: "popup",
        title: formTitle ? formTitle : "",
        context: setupContext(entity),
        error: (error: Error) => throwError(error.message),
        callback: (form: Form) => callback && callback(form)
    });
}
export async function openFormByHelper(
    formPath: string, helperEntityName: string, paramList: IParam[], showProgress?: boolean
) {
    const pid = showProgress ? mx.ui.showProgress("", true) : undefined;
    const helperEntity = await createThenCommit(helperEntityName, paramList);
    if (helperEntity) {
        openForm(formPath, helperEntity, "", () => {
            (pid !== undefined) && mx.ui.hideProgress(pid);
        });
    }
}
export function rollback(entity: MxObject): Promise<MxObject | undefined> {
    return new Promise(resolve => {
        mx.data.rollback({
            mxobj: entity,
            callback: () => resolve(entity),
            error: (error) => {
                error.message && throwError(error.message);
                resolve(undefined);
            }
        });
    });
}
//#endregion
//#region ------------------------------------- OTHER
export function subscribe(guid: any, callback: any) {
    return mx.data.subscribe({ guid, callback });
}
export function subscribeToClass(entity: string, callback: any) {
    return mx.data.subscribe({ entity, callback });
}
export function unsubscribe(handler: number) {
    return mx.data.unsubscribe(handler);
}
export function throwError(errorMessage: string, modal?: boolean) {
    return mx.ui.error(errorMessage, modal);
}
//#endregion
